import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { AxiosError } from 'axios'

import { axiosInstance } from '../helpers'
import {
    TenantMessage,
    SendMessageInput,
    AudiencePreviewInput,
    AudiencePreviewResponse,
    MessageRecipientsResponse,
    SelectableTenant,
    ThreadReply,
    ThreadRepliesResponse,
} from '../models/TenantMessage'
import { useUser } from './useUser'

const MESSAGES_KEY = 'tenant-messages'
const TENANTS_KEY = 'tenant-messages-tenants'
const RECIPIENTS_KEY = 'tenant-messages-recipients'
const REPLIES_KEY = 'tenant-messages-replies'
const BASE = '/beta/api/tenant-message'

const extractError = (error: unknown, fallback: string): string => {
    const data = (error as AxiosError)?.response?.data as
        | Record<string, unknown>
        | undefined

    if (data) {
        const nonField = data.non_field_errors
        if (Array.isArray(nonField) && nonField.length > 0) {
            return String(nonField[0])
        }
        const firstKey = Object.keys(data)[0]
        if (firstKey) {
            const val = data[firstKey]
            if (Array.isArray(val) && val.length > 0) {
                return `${firstKey}: ${val[0]}`
            }
            if (typeof val === 'string') {
                return val
            }
        }
    }

    return fallback
}

export type MessageOrdering =
    | '-last_activity'
    | 'last_activity'
    | '-created_date'
    | 'created_date'
    | '-subject'
    | 'subject'
    | '-recipient_count'
    | 'recipient_count'
    | '-latest_reply_at'
    | 'latest_reply_at'

export type DateRangePreset =
    | 'today'
    | 'this_week'
    | 'this_month'
    | 'last_30_days'
    | 'last_90_days'

export interface MessageFilters {
    initiated_by?: 'staff' | 'tenant'
    has_replies?: boolean
    unread_replies?: boolean
    audience_type?: 'ALL' | 'SELECTED'
    individual?: boolean
    channel?: 'email' | 'sms' | 'portal_only'
    read_status?: 'all_read' | 'partially_read' | 'unread'
    tenant_id?: number
    unit_ids?: number[]
    area_ids?: number[]
    search?: string
    created_by?: number
    created_after?: string
    created_before?: string
    date_range?: DateRangePreset
    archived?: 'true' | 'all'
    ordering?: MessageOrdering
    page?: number
    page_size?: number
}

interface PaginatedMessagesResponse {
    count: number
    next: string | null
    previous: string | null
    results: TenantMessage[]
}

export const useTenantMessages = (filters?: MessageFilters) => {
    const queryClient = useQueryClient()
    const { workspaceUser } = useUser()

    const apartmentId = workspaceUser?.active_workspace?.id
    const isApartment =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'

    const { data, isLoading, error, refetch } = useQuery<
        PaginatedMessagesResponse,
        Error
    >({
        queryKey: [MESSAGES_KEY, apartmentId, filters],
        queryFn: async () => {
            const params = new URLSearchParams()
            if (filters?.initiated_by) {
                params.append('initiated_by', filters.initiated_by)
            }
            if (filters?.has_replies !== undefined) {
                params.append('has_replies', String(filters.has_replies))
            }
            if (filters?.unread_replies) {
                params.append('unread_replies', 'true')
            }
            if (filters?.audience_type) {
                params.append('audience_type', filters.audience_type)
            }
            if (filters?.individual) {
                params.append('individual', 'true')
            }
            if (filters?.channel) {
                params.append('channel', filters.channel)
            }
            if (filters?.read_status) {
                params.append('read_status', filters.read_status)
            }
            if (filters?.tenant_id) {
                params.append('tenant_id', String(filters.tenant_id))
            }
            if (filters?.unit_ids && filters.unit_ids.length > 0) {
                params.append('unit_ids', filters.unit_ids.join(','))
            }
            if (filters?.area_ids && filters.area_ids.length > 0) {
                params.append('area_ids', filters.area_ids.join(','))
            }
            if (filters?.search) {
                params.append('search', filters.search)
            }
            if (filters?.created_by) {
                params.append('created_by', String(filters.created_by))
            }
            if (filters?.created_after) {
                params.append('created_after', filters.created_after)
            }
            if (filters?.created_before) {
                params.append('created_before', filters.created_before)
            }
            if (filters?.date_range) {
                params.append('date_range', filters.date_range)
            }
            if (filters?.archived) {
                params.append('archived', filters.archived)
            }
            if (filters?.ordering) {
                params.append('ordering', filters.ordering)
            }
            if (filters?.page) {
                params.append('page', String(filters.page))
            }
            if (filters?.page_size) {
                params.append('page_size', String(filters.page_size))
            }
            const qs = params.toString()
            const url = qs ? `${BASE}/?${qs}` : `${BASE}/`
            const res = await axiosInstance.get(url)
            const raw = res.data
            if (Array.isArray(raw)) {
                return {
                    count: raw.length,
                    next: null,
                    previous: null,
                    results: raw,
                }
            }
            if (raw && Array.isArray(raw.results)) {
                return {
                    count: raw.count ?? raw.results.length,
                    next: raw.next ?? null,
                    previous: raw.previous ?? null,
                    results: raw.results,
                }
            }
            return { count: 0, next: null, previous: null, results: [] }
        },
        enabled:
            isApartment &&
            !!apartmentId &&
            (filters?.tenant_id == null || filters.tenant_id > 0),
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const invalidate = () =>
        queryClient.invalidateQueries({ queryKey: [MESSAGES_KEY] })

    const sendMessage = useMutation({
        mutationFn: async (input: SendMessageInput) => {
            const { attachments, ...fields } = input

            if (attachments && attachments.length > 0) {
                const formData = new FormData()
                formData.append('subject', fields.subject)
                formData.append('body', fields.body)
                formData.append('audience_type', fields.audience_type)
                if (fields.lease_scope) {
                    formData.append('lease_scope', fields.lease_scope)
                }
                if (fields.client_token) {
                    formData.append('client_token', fields.client_token)
                }
                if (fields.send_email !== undefined) {
                    formData.append('send_email', String(fields.send_email))
                }
                if (fields.send_sms !== undefined) {
                    formData.append('send_sms', String(fields.send_sms))
                }
                if (fields.email_sender_name) {
                    formData.append(
                        'email_sender_name',
                        fields.email_sender_name,
                    )
                }
                if (fields.email_reply_to) {
                    formData.append('email_reply_to', fields.email_reply_to)
                }
                if (fields.lease_ids.length > 0) {
                    formData.append(
                        'lease_ids',
                        JSON.stringify(fields.lease_ids),
                    )
                }
                attachments.forEach((file) => {
                    formData.append('attachments', file)
                })

                const res = await axiosInstance.post<TenantMessage>(
                    `${BASE}/`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } },
                )
                return res.data
            }

            const res = await axiosInstance.post<TenantMessage>(
                `${BASE}/`,
                fields,
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Message sent')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to send message'))
        },
    })

    const audiencePreview = useMutation({
        mutationFn: async (body: AudiencePreviewInput) => {
            const res = await axiosInstance.post<AudiencePreviewResponse>(
                `${BASE}/audience-preview/`,
                body,
            )
            return res.data
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to preview audience'))
        },
    })

    const archiveMessage = useMutation({
        mutationFn: async (messageId: number) => {
            const res = await axiosInstance.post(
                `${BASE}/${messageId}/archive/`,
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Thread archived')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to archive'))
        },
    })

    const unarchiveMessage = useMutation({
        mutationFn: async (messageId: number) => {
            const res = await axiosInstance.post(
                `${BASE}/${messageId}/unarchive/`,
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Thread unarchived')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to unarchive'))
        },
    })

    const bulkUnarchive = useMutation({
        mutationFn: async (ids: number[]) => {
            const res = await axiosInstance.post(`${BASE}/bulk-unarchive/`, {
                ids,
            })
            return res.data
        },
        onSuccess: (data) => {
            invalidate()
            toast.success(
                `${data.count} thread${data.count !== 1 ? 's' : ''} unarchived`,
            )
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to unarchive'))
        },
    })

    const bulkArchive = useMutation({
        mutationFn: async (ids: number[]) => {
            const res = await axiosInstance.post(`${BASE}/bulk-archive/`, {
                ids,
            })
            return res.data
        },
        onSuccess: (data) => {
            invalidate()
            toast.success(
                `${data.count} thread${data.count !== 1 ? 's' : ''} archived`,
            )
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to archive'))
        },
    })

    return {
        messages: data?.results ?? [],
        totalCount: data?.count ?? 0,
        hasNextPage: !!data?.next,
        hasPreviousPage: !!data?.previous,
        isLoading,
        error,
        refetch,
        isApartment,
        sendMessage,
        audiencePreview,
        archiveMessage,
        unarchiveMessage,
        bulkUnarchive,
        bulkArchive,
    }
}

export const useTenantMessageTenants = () => {
    const { workspaceUser } = useUser()

    const apartmentId = workspaceUser?.active_workspace?.id
    const isApartment =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'

    const { data: tenants, isLoading } = useQuery<SelectableTenant[], Error>({
        queryKey: [TENANTS_KEY, apartmentId],
        queryFn: async () => {
            const res = await axiosInstance.get<SelectableTenant[]>(
                `${BASE}/tenants/`,
            )
            return res.data
        },
        enabled: isApartment && !!apartmentId,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    return { tenants: tenants ?? [], isLoading }
}

export const useTenantMessageRecipients = (messageId: number | null) => {
    const { data, isLoading } = useQuery<MessageRecipientsResponse, Error>({
        queryKey: [RECIPIENTS_KEY, messageId],
        queryFn: async () => {
            const res = await axiosInstance.get(
                `${BASE}/${messageId}/recipients/`,
            )
            const raw = res.data
            if (Array.isArray(raw)) {
                return { count: raw.length, recipients: raw }
            }
            if (raw && Array.isArray(raw.results)) {
                return {
                    count: raw.count ?? raw.results.length,
                    recipients: raw.results,
                }
            }
            if (raw && Array.isArray(raw.recipients)) {
                return raw
            }
            return { count: 0, recipients: [] }
        },
        enabled: !!messageId,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    return {
        recipients: data?.recipients ?? [],
        count: data?.count ?? 0,
        isLoading,
    }
}

export const useTenantMessageReplies = (
    messageId: number | null,
    tenantId?: number | null,
) => {
    const { data, isLoading, refetch } = useQuery<ThreadRepliesResponse, Error>(
        {
            queryKey: [REPLIES_KEY, messageId, tenantId ?? 'all'],
            queryFn: async () => {
                const params = tenantId ? `?tenant=${tenantId}` : ''
                const res = await axiosInstance.get(
                    `${BASE}/${messageId}/replies/${params}`,
                )
                const raw = res.data
                if (Array.isArray(raw)) {
                    return { count: raw.length, results: raw }
                }
                if (raw && Array.isArray(raw.results)) {
                    return {
                        count: raw.count ?? raw.results.length,
                        results: raw.results,
                    }
                }
                return { count: 0, results: [] }
            },
            enabled: !!messageId,
            refetchOnMount: true,
            refetchOnWindowFocus: false,
        },
    )

    return {
        replies: data?.results ?? [],
        count: data?.count ?? 0,
        isLoading,
        refetch,
    }
}

export const useReplyToMessage = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({
            messageId,
            body,
            recipient,
        }: {
            messageId: number
            body: string
            recipient: number
        }) => {
            const res = await axiosInstance.post<ThreadReply>(
                `${BASE}/${messageId}/reply/`,
                { body, recipient },
            )
            return res.data
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: [REPLIES_KEY, variables.messageId],
            })
            queryClient.invalidateQueries({ queryKey: [MESSAGES_KEY] })
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to send reply'))
        },
    })
}

export const useMarkRepliesRead = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (messageId: number) => {
            await axiosInstance.post(`${BASE}/${messageId}/mark-replies-read/`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [MESSAGES_KEY] })
        },
    })
}
